/**
 * App.tsx — Root application component
 *
 * PERFORMANCE: Route-based code splitting via React.lazy()
 * --------------------------------------------------------
 * All page components are lazy-loaded so the initial bundle only contains
 * the shell (providers, layout, router). Each page is fetched on demand
 * when the user navigates to its route, cutting ~40-60% off the initial
 * JS payload.
 *
 * HOW TO ADD A NEW PAGE:
 *   1. Add a lazy import below (keep alphabetical within each group).
 *   2. Add the <Route> inside <Routes> (above the catch-all "*" route).
 *   3. The Suspense boundary in AppRoutes handles the loading spinner.
 *
 * GROUPS:
 *   - Core pages:    Index, Browse, Journey, Essays, Profile, ScholarshipPage
 *   - Auth pages:    Auth, AuthCallback, ResetPassword
 *   - Feature pages: Achievements, Pricing, Checkout*, Guardian*, RecLetterToolkit
 *   - Utility pages: NotFound, ProfileVault
 */

import { useState, useCallback, lazy, Suspense } from "react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
// Sonner toast system removed — using Radix-based Toaster only (see H11 audit fix)
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { TourProvider } from "@/tour/components/TourProvider";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { CompareProvider } from "@/contexts/CompareContext";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import KeyboardShortcutsHelp from "@/components/ui/KeyboardShortcutsHelp";
import CompareBar from "@/components/compare/CompareBar";
import CompareDrawer from "@/components/compare/CompareDrawer";
import FeedbackWidget from "@/components/FeedbackWidget";
import InstallPrompt from "@/components/pwa/InstallPrompt";
import PostHogTracker from "@/components/analytics/PostHogTracker";
import ProtectedRoute from "@/components/ProtectedRoute";

// ---------------------------------------------------------------------------
// Lazy-loaded page components (code-split into separate chunks)
// ---------------------------------------------------------------------------

/**
 * lazyRetry — wraps dynamic import() with a single retry + cache-busted
 * re-import on chunk load failure. After a new deploy the old chunk hashes
 * no longer exist on the CDN, so browsers that still have the old manifest
 * in memory will fail to fetch them.
 *
 * Strategy:
 *   1. First attempt: normal dynamic import().
 *   2. On failure: retry the import with a cache-busting query param.
 *      This forces the browser to bypass any cached 404 responses.
 *   3. If retry fails: do a full page reload (once per session) to pick
 *      up the new HTML + manifest. This preserves the full URL including
 *      hash fragments — critical for OAuth callbacks like /auth/callback
 *      where the access token lives in the URL fragment.
 *   4. If we already reloaded: re-throw so the ErrorBoundary catches it.
 */
function lazyRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() =>
    factory().catch(() =>
      // Retry once with cache-bust before resorting to a full reload
      factory().catch((err: unknown) => {
        const key = "sv-chunk-reload";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          // Use href assignment (not reload()) to preserve the full URL
          // including hash fragments needed by Supabase auth callbacks
          window.location.href = window.location.href;
          // Return a never-resolving promise so React doesn't render
          // a broken component while the page is reloading
          return new Promise<{ default: T }>(() => {});
        }
        throw err;
      })
    )
  );
}

// Core pages — the main user-facing views
const Index = lazyRetry(() => import("./pages/Index"));
const Browse = lazyRetry(() => import("./pages/Browse"));
const CollegeExplorer = lazyRetry(() => import("./pages/CollegeExplorer"));
const Journey = lazyRetry(() => import("./pages/Journey"));
const Essays = lazyRetry(() => import("./pages/Essays"));
const Profile = lazyRetry(() => import("./pages/Profile"));
const ProfileVault = lazyRetry(() => import("./pages/ProfileVault"));
const ScholarshipPage = lazyRetry(() => import("./pages/ScholarshipPage"));

// Auth pages — login, signup, password reset, callback
const Auth = lazyRetry(() => import("./pages/Auth"));
const AuthCallback = lazyRetry(() => import("./pages/AuthCallback"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));

// Feature pages — secondary features & flows
const Achievements = lazyRetry(() => import("./pages/Achievements"));
const Pricing = lazyRetry(() => import("./pages/Pricing"));
const CheckoutSuccess = lazyRetry(() => import("./pages/CheckoutSuccess"));
const CheckoutCancel = lazyRetry(() => import("./pages/CheckoutCancel"));
const GuardianAccept = lazyRetry(() => import("./pages/GuardianAccept"));
const GuardianDashboard = lazyRetry(() => import("./pages/GuardianDashboard"));
const RecLetterToolkit = lazyRetry(() => import("./pages/RecLetterToolkit"));
const DocumentVault = lazyRetry(() => import("./pages/DocumentVault"));

// Content/SEO pages — public guides and resources
const ScholarshipGuide = lazyRetry(() => import("./pages/ScholarshipGuide"));
const ScholarshipCalendar = lazyRetry(() => import("./pages/ScholarshipCalendar"));

// Legal pages
const Privacy = lazyRetry(() => import("./pages/Privacy"));
const Terms = lazyRetry(() => import("./pages/Terms"));

// Utility pages
const NotFound = lazyRetry(() => import("./pages/NotFound"));

// ---------------------------------------------------------------------------
// Shared loading fallback for Suspense boundaries
// ---------------------------------------------------------------------------

/**
 * PageLoadingFallback — shown while a lazy-loaded page chunk is being fetched.
 * Uses the app's design tokens (ds-bg, ds-red) for visual consistency.
 */
function PageLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-ds-bg">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-ds-red border-t-transparent rounded-full animate-spin" />
        <p className="text-ds-text-secondary text-sm">Loading…</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal components
// ---------------------------------------------------------------------------

const queryClient = new QueryClient();

/** Renders inside BrowserRouter so hooks like useNavigate work. */
function KeyboardShortcutsManager({ children }: { children: React.ReactNode }) {
  const [showHelp, setShowHelp] = useState(false);
  const toggleHelp = useCallback(() => setShowHelp((prev) => !prev), []);
  useKeyboardShortcuts({ onToggleHelp: toggleHelp });

  return (
    <>
      {children}
      <KeyboardShortcutsHelp open={showHelp} onClose={() => setShowHelp(false)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Root App component
// ---------------------------------------------------------------------------

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" storageKey="theme">
     <AuthProvider>
       <ProfileProvider>
        <SubscriptionProvider>
      <TooltipProvider>
        <Toaster />
        <BrowserRouter>
          <PostHogTracker />
          <CompareProvider>
          <TourProvider>
          <KeyboardShortcutsManager>
          {/* Suspense boundary wraps all lazy routes */}
          <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            {/* ── Core pages ── */}
            <Route path="/" element={<Index />} />
            <Route path="/browse" element={<Browse />} />
            <Route path="/colleges" element={<CollegeExplorer />} />
            <Route path="/journey" element={<ProtectedRoute><Journey /></ProtectedRoute>} />
            <Route path="/journey/:view" element={<ProtectedRoute><Journey /></ProtectedRoute>} />
            <Route path="/essays" element={<ProtectedRoute><Essays /></ProtectedRoute>} />
            <Route path="/essays/:tab" element={<ProtectedRoute><Essays /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/my-profile" element={<ProtectedRoute><ProfileVault /></ProtectedRoute>} />
            <Route path="/my-profile/:tab" element={<ProtectedRoute><ProfileVault /></ProtectedRoute>} />
            <Route path="/scholarship/:id" element={<ScholarshipPage />} />

            {/* ── Auth pages ── */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />

            {/* ── Feature pages ── */}
            <Route path="/guardian/accept/:inviteCode" element={<GuardianAccept />} />
            <Route path="/guardian/dashboard" element={<ProtectedRoute><GuardianDashboard /></ProtectedRoute>} />
            <Route path="/resources/rec-letters" element={<ProtectedRoute><RecLetterToolkit /></ProtectedRoute>} />
            <Route path="/resources/document-vault" element={<ProtectedRoute><DocumentVault /></ProtectedRoute>} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/guide" element={<ScholarshipGuide />} />
            <Route path="/calendar" element={<ScholarshipCalendar />} />
            <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccess /></ProtectedRoute>} />
            <Route path="/checkout/cancel" element={<CheckoutCancel />} />
            <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />

            {/* ── Legal pages ── */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            {/* ── Catch-all (ADD ALL CUSTOM ROUTES ABOVE THIS LINE) ── */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </KeyboardShortcutsManager>
          </TourProvider>
          <CompareBar />
          <CompareDrawer />
          <FeedbackWidget />
          <InstallPrompt />
          </CompareProvider>
        </BrowserRouter>
      </TooltipProvider>
        </SubscriptionProvider>
       </ProfileProvider>
     </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
